/**
 * The new-litter wizard: the litter, then the puppies in it.
 *
 * Two steps and not one form, because they are two different moments. The
 * litter is entered once, calmly, often before the puppies arrive; the puppies
 * are entered in a whelping room at four in the morning, one hand free, and that
 * screen has to be nothing but collars and sexes.
 *
 * **The litter is written at the end of step one, not at the end of both.** It
 * costs nothing — a Firestore write is durable and echoed back locally before
 * the next frame, offline included (see api/mutations.ts) — and it means a phone
 * that dies between the steps has lost the puppies, not the litter. The puppies
 * are then addable from the litter's own page, which is a place the breeder is
 * going anyway.
 *
 * An `incoming` litter has no step two at all. There are no puppies to describe
 * yet; `expectedCount` is the whole of what is known, and asking for seven
 * collars for animals that have not been born is asking for seven answers that
 * will all be wrong.
 */
import { canWriteLitters } from '@puppy-doctor/platform';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronLeft, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { createLitter, createOffspring } from '@/api/mutations.ts';
import { useTenantDoc } from '@/api/tenant.ts';
import {
  DEFAULT_COUNT,
  LitterForm,
  MAX_COUNT,
  blankLitter,
} from '@/components/LitterForm.tsx';
import {
  birthWeightOf,
  type Draft,
  DraftCard,
  draftAt,
  isComplete,
} from '@/components/OffspringDraft.tsx';
import { Button } from '@/components/ui.tsx';
import { useTenant, useTenantId } from '@/tenant/TenantProvider.tsx';

export const Route = createFileRoute('/litters/new')({
  component: NewLitter,
});

function NewLitter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const { role } = useTenant();
  // Only to seed the breed field. The tenant document is already subscribed in
  // `__root.tsx` and persisted with the query cache, so it is here on the first
  // render in practice; if it somehow is not, the field opens blank, which is a
  // legal answer for an optional field and not worth a spinner over.
  const { data: tenant } = useTenantDoc(tenantId);

  const [litterId, setLitterId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);

  if (role === null || !canWriteLitters(role)) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink />
        <p className="text-ink-faint">{t('litter.readOnly')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <BackLink />
        <h1 className="mt-1 text-2xl font-bold tracking-[-0.02em]">{t('litter.newTitle')}</h1>
        <p className="mt-1 text-sm text-ink-faint">
          {t('litter.step', { current: litterId === null ? 1 : 2, total: 2 })}
        </p>
      </div>

      {litterId === null ? (
        <LitterForm
          tenantId={tenantId}
          initial={blankLitter(tenant?.breed ?? '')}
          mode="create"
          submitLabel={(status) =>
            status === 'incoming' ? t('litter.saveIncoming') : t('litter.toOffspring')
          }
          onSubmit={(fields) => {
            const created = createLitter({ tenantId, ...fields });

            // An incoming litter is finished here: there are no puppies to
            // describe.
            if (fields.status === 'incoming') {
              void navigate({ to: '/litters/$litterId', params: { litterId: created } });
              return;
            }

            setLitterId(created);
            setDrafts(
              Array.from({ length: fields.expectedCount ?? DEFAULT_COUNT }, (_, index) =>
                draftAt(index),
              ),
            );
          }}
        />
      ) : (
        <OffspringStep
          tenantId={tenantId}
          litterId={litterId}
          drafts={drafts}
          setDrafts={setDrafts}
        />
      )}
    </div>
  );
}

const BackLink = () => {
  const { t } = useTranslation();

  return (
    <Link
      to="/"
      className="-ml-1 inline-flex items-center gap-1 text-sm text-ink-soft transition-colors hover:text-ink"
    >
      <ChevronLeft className="size-4" aria-hidden />
      {t('nav.litters')}
    </Link>
  );
};

// ---------------------------------------------------------------------------
// Step two: the puppies
// ---------------------------------------------------------------------------

function OffspringStep({
  tenantId,
  litterId,
  drafts,
  setDrafts,
}: {
  tenantId: string;
  litterId: string;
  drafts: readonly Draft[];
  setDrafts: (drafts: Draft[]) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showErrors, setShowErrors] = useState(false);

  const patch = (key: string, change: Partial<Draft>) =>
    setDrafts(drafts.map((draft) => (draft.key === key ? { ...draft, ...change } : draft)));

  // Always required here: step two does not exist for an `incoming` litter —
  // see the note at the top of this file — so every puppy on this screen has
  // been born and been on a scale.
  const incomplete = drafts.filter((draft) => !isComplete(draft, true)).length;

  const save = () => {
    if (incomplete > 0) {
      setShowErrors(true);
      return;
    }

    drafts.forEach((draft, index) => {
      // Narrowed by `incomplete` above; the schema will not take null for
      // either of these and the parse would throw on the way back in.
      if (draft.sex === null || draft.markerColor === null) return;

      createOffspring({
        tenantId,
        litterId,
        name: draft.name,
        sex: draft.sex,
        markerColor: draft.markerColor,
        sequence: index,
        birthWeightGrams: birthWeightOf(draft),
        photoPath: draft.photoPath,
      });
    });

    // Not awaited, and there is nothing to await: every one of those writes is
    // already in the local cache and the litter page's listener has it.
    void navigate({ to: '/litters/$litterId', params: { litterId } });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-soft">{t('offspring.stepLead')}</p>

      {drafts.map((draft, index) => (
        <DraftCard
          key={draft.key}
          tenantId={tenantId}
          draft={draft}
          index={index}
          // Every other collar in the litter, so the picker can say which are
          // already spoken for without stopping anybody using one twice.
          taken={drafts
            .filter((other) => other.key !== draft.key)
            .map((other) => other.markerColor)
            .filter((hex): hex is string => hex !== null)}
          needsBirthWeight
          showErrors={showErrors}
          onChange={(change) => patch(draft.key, change)}
          onRemove={
            drafts.length > 1
              ? () => setDrafts(drafts.filter((other) => other.key !== draft.key))
              : undefined
          }
        />
      ))}

      <Button
        variant="secondary"
        full
        className="gap-1.5"
        onClick={() => setDrafts([...drafts, draftAt(drafts.length)])}
        disabled={drafts.length >= MAX_COUNT}
      >
        <Plus className="size-4" aria-hidden />
        {t('offspring.addAnother')}
      </Button>

      {showErrors && incomplete > 0 && (
        <p role="alert" className="text-sm text-alert">
          {t('offspring.incomplete', { count: incomplete })}
        </p>
      )}

      <Button full onClick={save}>
        {t('offspring.saveAll', { count: drafts.length })}
      </Button>
    </div>
  );
}
